(() => {
  if (window.mobaiGroupPickerInitialized) return;
  window.mobaiGroupPickerInitialized = true;
  let controller = null;
  let generation = 0;
  let pendingChoice = null;
  let stickyCleanup = () => {};
  let blockedButtons = new Map();
  const section = () => document.querySelector('[data-group-product-section]');
  const picker = () => section()?.querySelector('[data-group-picker]');
  const isHairpin = element => element?.querySelector('[data-group-picker]')?.dataset.groupFamily === 'H01' && ['9667825041647', '9667825500399', '9667953098991'].includes(element.dataset.groupProduct);
  const isC01 = element => element?.hasAttribute('data-c01-template') && element.querySelector('[data-group-picker]')?.dataset.groupFamily === 'C01' && ['9669107745007', '9669107974383', '9669108138223', '9669108302063', '9667826090223'].includes(element.dataset.groupProduct);
  const usesSnapshots = element => element?.hasAttribute('data-choice-snapshot-enabled') && (isHairpin(element) || isC01(element)) && /^(ar|en|zh(?:-.+)?)$/i.test(element.dataset.groupLocale || '');
  function createC01RequestCache({ fetcher = (...args) => fetch(...args), now = () => Date.now(), setTimer = setTimeout, clearTimer = clearTimeout } = {}) {
    const cache = new Map();
    const inflight = new Map();
    const keyFor = (url, context) => JSON.stringify([String(url), context.locale || '', context.currency || '', context.theme || '', context.country || '']);
    const abortError = () => { const error = new Error('group_request_aborted'); error.name = 'AbortError'; return error; };
    const waitFor = (promise, signal) => {
      if (!signal) return promise;
      if (signal.aborted) return Promise.reject(abortError());
      return new Promise((resolve, reject) => {
        const abort = () => { signal.removeEventListener('abort', abort); reject(abortError()); };
        signal.addEventListener('abort', abort, { once: true });
        promise.then(value => { signal.removeEventListener('abort', abort); resolve(value); }, error => { signal.removeEventListener('abort', abort); reject(error); });
        if (signal.aborted) abort();
      });
    };
    return {
      load(url, context = {}, consumerSignal) {
        if (consumerSignal?.aborted) return Promise.reject(abortError());
        const key = keyFor(url, context);
        for (const [entryKey, entry] of cache) if (entry.expiresAt <= now()) cache.delete(entryKey);
        const cached = cache.get(key);
        if (cached) {
          cache.delete(key); cache.set(key, cached);
          return waitFor(Promise.resolve(cached.html), consumerSignal);
        }
        let entry = inflight.get(key);
        if (!entry) {
          const controller = new AbortController();
          let timeout;
          const timedOut = new Promise((resolve, reject) => {
            timeout = setTimer(() => { controller.abort(); reject(new Error('group_request_timeout')); }, 20000);
          });
          const responseText = Promise.resolve().then(() => fetcher(String(url), { credentials: 'same-origin', cache: 'no-store', signal: controller.signal })).then(response => {
            if (!response.ok) throw new Error('group_http_' + response.status);
            return response.text();
          });
          entry = {};
          const ownEntry = entry;
          entry.promise = Promise.race([responseText, timedOut]).then(html => {
            cache.delete(key);
            cache.set(key, { html, expiresAt: now() + 15000 });
            while (cache.size > 5) cache.delete(cache.keys().next().value);
            return html;
          }).finally(() => {
            clearTimer(timeout);
            if (inflight.get(key) === ownEntry) inflight.delete(key);
          });
          inflight.set(key, entry);
        }
        return waitFor(entry.promise, consumerSignal);
      },
      invalidate(url, context = {}) { cache.delete(keyFor(url, context)); }
    };
  }
  const c01Requests = createC01RequestCache();
  const prepareChoiceIntent = event => {
    if (event.type === 'pointerover' && event.pointerType !== 'mouse') return;
    const choice = event.target.closest?.('[data-group-choice]');
    if (!choice || !isHairpin(choice.closest('[data-group-product-section]')) || !choice.dataset.groupImage) return;
    window.mobaiH01Media?.prepare({ src: choice.dataset.groupImage, srcset: choice.dataset.groupImageSrcset || '', sizes: choice.dataset.groupImageSizes || '' }, 'low').catch(() => {});
  };
  ['pointerover', 'pointerdown', 'focusin'].forEach(type => document.addEventListener(type, prepareChoiceIntent, { passive: true }));
  const currentURL = () => new URL(location.href);
  const nativeDisabled = button => button.hasAttribute('data-group-guard-disabled') ? button.dataset.groupGuardDisabled === 'true' : button.disabled;
  const releaseInitialGuard = element => {
    element?.querySelectorAll('[data-group-guard-disabled]').forEach(button => {
      button.disabled = button.dataset.groupGuardDisabled === 'true';
      button.removeAttribute('data-group-guard-disabled');
    });
  };
  const releaseInitialCapture = () => { window.mobaiGroupInitialGuardCleanup?.(); };
  const nativeURL = (value, fieldset = picker()) => {
    const result = new URL(value, location.origin);
    if (result.origin !== location.origin || !/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?products\//i.test(result.pathname)) throw new Error('group_product_url');
    const root = (fieldset?.dataset.groupRoot || '/').replace(/\/$/, '');
    if (root && result.pathname.startsWith('/products/')) result.pathname = root + result.pathname;
    return result;
  };
  const c01Context = element => ({ locale: element.dataset.groupLocale || '', currency: element.dataset.groupCurrency || '', theme: currentURL().searchParams.get('preview_theme_id') || element.dataset.groupPreviewTheme || '', country: currentURL().searchParams.get('country') || '' });
  const prepareC01URL = (url, element) => {
    const current = currentURL();
    for (const key of ['country', 'currency']) if (current.searchParams.has(key) && !url.searchParams.has(key)) url.searchParams.set(key, current.searchParams.get(key));
    const preview = current.searchParams.get('preview_theme_id') || element.dataset.groupPreviewTheme;
    if (preview) url.searchParams.set('preview_theme_id', preview);
    url.searchParams.sort();
    return url;
  };
  const prepareC01Intent = event => {
    if (event.type === 'pointerover' && event.pointerType !== 'mouse') return;
    const choice = event.target.closest?.('[data-group-choice]');
    const element = choice?.closest('[data-group-product-section]');
    if (usesSnapshots(element)) return;
    if (!choice || !isC01(element) || choice.dataset.groupHandle === picker()?.dataset.groupCurrent) return;
    const requested = nativeURL(choice.href, picker());
    requested.searchParams.set('section_id', picker().dataset.groupSectionId);
    prepareC01URL(requested, element);
    c01Requests.load(requested, c01Context(element)).catch(() => {});
  };
  ['pointerover', 'focusin'].forEach(type => document.addEventListener(type, prepareC01Intent, { passive: true }));
  const setBusy = (busy, message = '') => {
    const element = section();
    if (!element) return;
    element.mobaiGroupInitialGuardStop?.();
    element.dataset.groupPending = String(busy);
    element.setAttribute('aria-busy', String(busy));
    if (busy) {
      blockedButtons = new Map([...element.querySelectorAll('[data-add-button],[data-mobile-buybar-button]')].map(button => [button, nativeDisabled(button)]));
      blockedButtons.forEach((_, button) => { button.disabled = true; });
    } else {
      blockedButtons.forEach((disabled, button) => { if (button.isConnected) button.disabled = disabled; });
      blockedButtons.clear();
      releaseInitialGuard(element);
    }
    const status = element.querySelector('[data-group-status]');
    if (status) { status.textContent = message; status.hidden = !message; }
  };
  const caption = () => document.dispatchEvent(new Event('mobai:product-image-change'));
  const syncSaved = () => {
    let saved = [];
    try { saved = JSON.parse(localStorage.getItem('mobai-native-saved') || '[]'); } catch {}
    if (!Array.isArray(saved)) saved = [];
    section()?.querySelectorAll('[data-save]').forEach(button => {
      const active = saved.includes(button.dataset.save);
      button.classList.toggle('saved', active);
      button.setAttribute('aria-pressed', String(active));
    });
  };
  const initializeSticky = () => {
    stickyCleanup();
    const element = section(), bar = element?.querySelector('[data-mobile-buybar]'), button = element?.querySelector('[data-add-button]'), sticky = element?.querySelector('[data-mobile-buybar-button]');
    if (!bar || !button || !sticky) { stickyCleanup = () => {}; return; }
    const sync = () => {
      sticky.disabled = button.disabled;
      sticky.textContent = button.textContent.trim();
      const price = element.querySelector('[data-product-price]'), stickyPrice = element.querySelector('[data-mobile-buybar-price]');
      if (price && stickyPrice) stickyPrice.textContent = price.textContent.trim();
    };
    sync();
    const observer = typeof MutationObserver === 'function' ? new MutationObserver(sync) : null;
    observer?.observe(button, { attributes: true, childList: true, subtree: true });
    const brandLayout = element.hasAttribute('data-hairpin-brand');
    const brandViewport = brandLayout ? window.matchMedia('(max-width: 749px)') : null;
    let visibilityFrame = 0;
    const updateBrandVisibility = () => {
      visibilityFrame = 0;
      if (!brandLayout) return;
      const visible = brandViewport.matches && button.getBoundingClientRect().bottom <= 0;
      bar.classList.toggle('is-hidden', !visible);
      bar.setAttribute('aria-hidden', String(!visible));
      bar.inert = !visible;
    };
    const scheduleBrandVisibility = () => {
      if (brandLayout && !visibilityFrame) visibilityFrame = requestAnimationFrame(updateBrandVisibility);
    };
    const intersection = typeof IntersectionObserver === 'function' ? new IntersectionObserver(([entry]) => {
      if (brandLayout) scheduleBrandVisibility();
      else bar.classList.toggle('is-hidden', entry.isIntersecting && entry.intersectionRatio >= .9);
    }, { threshold: [0, .9, 1] }) : null;
    intersection?.observe(button);
    if (brandLayout) {
      window.addEventListener('scroll', scheduleBrandVisibility, { passive: true });
      window.addEventListener('resize', scheduleBrandVisibility);
      window.addEventListener('load', scheduleBrandVisibility);
      brandViewport.addEventListener('change', scheduleBrandVisibility);
      updateBrandVisibility();
    }
    const onChange = () => requestAnimationFrame(() => { sync(); scheduleBrandVisibility(); });
    element.addEventListener('change', onChange);
    stickyCleanup = () => {
      observer?.disconnect(); intersection?.disconnect(); element.removeEventListener('change', onChange);
      if (brandLayout) {
        window.removeEventListener('scroll', scheduleBrandVisibility);
        window.removeEventListener('resize', scheduleBrandVisibility);
        window.removeEventListener('load', scheduleBrandVisibility);
        brandViewport.removeEventListener('change', scheduleBrandVisibility);
        if (visibilityFrame) cancelAnimationFrame(visibilityFrame);
      }
    };
  };
  const choices = () => [...(picker()?.querySelectorAll('[data-group-choice]') || [])];
  // H01 members are separate products: navigate the complete document so its
  // title, canonical, translated metadata and purchase form stay on one product.
  const hairpinURL = choice => {
    const target = nativeURL(choice.href), previous = currentURL();
    const productParams = new URLSearchParams(target.search);
    target.search = previous.search;
    ['variant', 'piece', 'section_id', 'sections', 'selling_plan', 'quantity'].forEach(key => target.searchParams.delete(key));
    productParams.forEach((value, key) => {
      if (!['piece', 'section_id', 'sections', 'selling_plan', 'quantity'].includes(key)) target.searchParams.set(key, value);
    });
    target.searchParams.set('variant', choice.dataset.groupVariant);
    const preview = previous.searchParams.get('preview_theme_id') || section()?.dataset.groupPreviewTheme;
    if (preview) target.searchParams.set('preview_theme_id', preview);
    target.hash = '';
    return target;
  };
  const snapshotSelector = 'title,meta[name="description"],meta[property^="og:"],meta[name^="twitter:"],link[rel="canonical"],link[rel="alternate"][hreflang],link[data-group-hero-preload]';
  const snapshotCopies = new Map();
  const snapshotHeads = new Map();
  let committedSnapshotURL = null;
  const snapshotBank = () => document.querySelector('[data-group-snapshot-bank]');
  const captureHead = source => [...source.querySelectorAll(snapshotSelector)].map(node => node.cloneNode(true));
  const cleanSnapshot = element => {
    element.querySelectorAll('script:not([type="application/ld+json"]):not([data-group-product-schema])').forEach(script => script.remove());
    element.querySelectorAll('[data-group-product-schema]').forEach(script => { script.type = 'application/ld+json'; });
    element.dataset.groupPending = 'false';
    element.removeAttribute('aria-busy');
    releaseInitialGuard(element);
    return element;
  };
  const registerSnapshots = () => {
    const active = section(), bank = snapshotBank();
    if (!usesSnapshots(active) || !bank) return false;
    if (!snapshotCopies.has(active.dataset.groupProduct)) {
      if (isHairpin(active)) {
        const hero = active.querySelector('#mobai-main-image')?.getAttribute('src');
        document.head.querySelectorAll('link[rel="preload"][as="image"]').forEach(link => {
          if (hero && new URL(link.getAttribute('href'), location.origin).href === new URL(hero, location.origin).href) link.setAttribute('data-group-hero-preload', '');
        });
      }
      snapshotCopies.set(active.dataset.groupProduct, cleanSnapshot(active.cloneNode(true)));
      snapshotHeads.set(active.dataset.groupProduct, captureHead(document.head));
    }
    for (const template of bank.querySelectorAll('template[data-group-snapshot]')) {
      const id = template.dataset.groupSnapshot;
      if (snapshotCopies.has(id)) continue;
      const content = template.content.querySelector('[data-group-product-section]');
      const head = bank.querySelector(`template[data-group-head="${id}"]`);
      if (content && head) {
        snapshotCopies.set(id, cleanSnapshot(content.cloneNode(true)));
        snapshotHeads.set(id, captureHead(head.content));
      }
    }
    return true;
  };
  const nativeHeadFor = (nodes, choice, targetURL, target) => {
    const next = nodes.map(node => node.cloneNode(true));
    const canonical = next.find(node => node.matches('link[rel="canonical"]'));
    const title = next.find(node => node.matches('title'));
    if (!canonical || !title?.textContent.trim()) throw new Error('group_snapshot_head');
    const canonicalURL = new URL(canonical.getAttribute('href'), location.origin);
    if (canonicalURL.origin !== location.origin || canonicalURL.pathname !== targetURL.pathname) throw new Error('group_snapshot_canonical');
    if (!next.some(node => node.matches('link[rel="alternate"][hreflang]'))) {
      for (const source of document.head.querySelectorAll('link[rel="alternate"][hreflang]')) {
        const alternate = source.cloneNode(true), url = new URL(source.href, location.origin);
        if (!/\/products\/[^/]+\/?$/.test(url.pathname)) continue;
        url.pathname = url.pathname.replace(/\/products\/[^/]+\/?$/, '/products/' + encodeURIComponent(choice.dataset.groupHandle));
        alternate.href = url.href;
        next.push(alternate);
      }
    }
    if (isHairpin(target) && !next.some(node => node.matches('link[data-group-hero-preload]'))) {
      const hero = target.querySelector('#mobai-main-image'), preload = document.createElement('link');
      preload.rel = 'preload'; preload.setAttribute('as', 'image');
      preload.href = hero.getAttribute('src');
      if (hero.hasAttribute('srcset')) preload.setAttribute('imagesrcset', hero.getAttribute('srcset'));
      if (hero.hasAttribute('sizes')) preload.setAttribute('imagesizes', hero.getAttribute('sizes'));
      preload.setAttribute('fetchpriority', 'high'); preload.setAttribute('data-group-hero-preload', '');
      next.push(preload);
    }
    return next;
  };
  const validateSnapshot = (target, choice, active, bank) => {
    const targetPicker = target?.querySelector('[data-group-picker]');
    const activePicker = active?.querySelector('[data-group-picker]');
    const expected = choice.dataset;
    if (!target || !bank || !usesSnapshots(target) || !activePicker || !targetPicker ||
      target.dataset.groupProduct !== expected.groupProduct || target.dataset.groupCurrentHandle !== expected.groupHandle ||
      targetPicker.dataset.groupCurrent !== expected.groupHandle || targetPicker.dataset.groupFamily !== activePicker.dataset.groupFamily ||
      bank.dataset.groupFamily !== activePicker.dataset.groupFamily) throw new Error('group_snapshot_product');
    for (const key of ['groupLocale', 'groupCurrency', 'groupCountry', 'groupPreviewTheme']) {
      if ((target.dataset[key] || '') !== (active.dataset[key] || '') || (bank.dataset[key] || '') !== (active.dataset[key] || '')) throw new Error('group_snapshot_context');
    }
    const form = target.querySelector('#mobai-product-form');
    if (!form || form.querySelector('[name=id]')?.value !== expected.groupVariant) throw new Error('group_snapshot_variant');
    if (!target.querySelector('h1')?.textContent.trim() || !target.querySelector('#mobai-main-image')?.getAttribute('src')) throw new Error('group_snapshot_content');
    const current = currentURL();
    if (current.searchParams.has('currency') && current.searchParams.get('currency') !== active.dataset.groupCurrency) throw new Error('group_snapshot_market');
    if (current.searchParams.has('country') && current.searchParams.get('country') !== active.dataset.groupCountry) throw new Error('group_snapshot_market');
    const preview = current.searchParams.get('preview_theme_id') || active.dataset.groupPreviewTheme || '';
    if (preview !== (active.dataset.groupPreviewTheme || '')) throw new Error('group_snapshot_theme');
    return form;
  };
  const replaceHead = nodes => {
    document.head.querySelectorAll(snapshotSelector).forEach(node => node.remove());
    for (const node of nodes) document.head.append(document.importNode(node, true));
  };
  const snapshotAddPending = () => section()?.querySelector('#mobai-product-form')?.dataset.cartAddPending === 'true';
  const normalizeSnapshotLinks = () => choices().forEach(choice => { choice.href = hairpinURL(choice).href; });
  const qaSnapshot = () => currentURL().searchParams.get('utm_campaign') === 'product_choice_execution_20261020';
  const markSnapshotImage = (element, started) => {
    if (!qaSnapshot()) return;
    const image = element.querySelector('#mobai-main-image');
    if (!image) return;
    const ready = () => {
      if (element.isConnected && image.naturalWidth > 0) {
        element.dataset.groupImageReady = 'true';
        element.dataset.groupImageMs = String(Math.round(performance.now() - started));
      }
    };
    if (image.complete && image.naturalWidth) ready(); else image.addEventListener('load', ready, { once: true });
  };
  const switchSnapshot = (choice, { updateHistory = true, replaceHistory = false, focus = true, restoring = false } = {}) => {
    const active = section(), oldPicker = picker();
    if (!choice || !usesSnapshots(active) || snapshotAddPending()) return false;
    const started = performance.now();
    const previousURL = currentURL().href;
    const previousHead = captureHead(document.head);
    let imported, changed = false, historyChanged = false;
    try {
      registerSnapshots();
      const target = snapshotCopies.get(choice.dataset.groupProduct);
      const head = snapshotHeads.get(choice.dataset.groupProduct);
      if (!target || !head) throw new Error('group_snapshot_missing');
      const next = document.importNode(target, true);
      validateSnapshot(next, choice, active, snapshotBank());
      const targetURL = hairpinURL(choice), nextHead = nativeHeadFor(head, choice, targetURL, next);
      controller?.abort(); generation++; pendingChoice = null;
      setBusy(true);
      stickyCleanup();
      if (updateHistory) {
        const replace = replaceHistory || targetURL.href === previousURL;
        history[replace ? 'replaceState' : 'pushState']({ mobaiNativeProduct: choice.dataset.groupProduct }, '', targetURL);
        historyChanged = true;
      }
      imported = next;
      active.replaceWith(imported); changed = true;
      replaceHead(nextHead);
      blockedButtons.clear(); setBusy(false); releaseInitialCapture();
      const enquiry = imported.querySelector('.mobai-whatsapp-product'), floating = document.querySelector('[data-whatsapp-float]');
      if (enquiry && floating) floating.href = enquiry.href;
      normalizeSnapshotLinks();
      document.querySelectorAll('form[action*="/localization"] input[name="return_to"]').forEach(field => { field.value = targetURL.pathname + targetURL.search; });
      initializeSticky(); caption(); syncSaved();
      if (qaSnapshot()) {
        imported.dataset.groupSwitchKind = 'snapshot';
        imported.dataset.groupSwitchMs = String(Math.round(performance.now() - started));
        imported.dataset.groupValidatedProduct = choice.dataset.groupProduct;
        imported.dataset.groupValidatedVariant = choice.dataset.groupVariant;
        imported.dataset.groupValidatedPrice = imported.querySelector('[data-product-price]')?.textContent.trim() || '';
      }
      markSnapshotImage(imported, started);
      if (focus) imported.querySelector(`[data-group-handle="${choice.dataset.groupHandle}"]`)?.focus({ preventScroll: true });
      document.dispatchEvent(new CustomEvent('mobai:product-choice', { detail: {
        productId: choice.dataset.groupProduct, variantId: choice.dataset.groupVariant,
        family: oldPicker.dataset.groupFamily, productTitle: imported.querySelector('h1').textContent.trim(), productURL: targetURL.href
      } }));
      committedSnapshotURL = targetURL.href;
      return true;
    } catch (error) {
      if (changed) { imported.replaceWith(active); replaceHead(previousHead); }
      if (historyChanged) history.replaceState({}, '', previousURL);
      setBusy(restoring, restoring ? oldPicker.dataset.groupFailed : ''); initializeSticky();
      if (qaSnapshot()) section().dataset.groupSwitchError = error.message;
      const status = picker()?.querySelector('[data-group-status]'), fallback = picker()?.querySelector('[data-group-fallback]');
      if (status) { status.textContent = oldPicker.dataset.groupFailed; status.hidden = false; }
      if (fallback) { fallback.href = hairpinURL(choice).href; fallback.hidden = false; }
      return false;
    }
  };
  const restoreHairpinLocation = () => {
    if (!currentURL().searchParams.has('piece')) return false;
    const requested = currentURL().searchParams.get('piece');
    const choice = choices().find(item => item.dataset.groupHandle === requested)
      || choices().find(item => item.dataset.groupHandle === picker().dataset.groupCurrent);
    if (!choice) return false;
    // Replace legacy partial-switch URLs rather than adding a redirect to Back.
    location.replace(hairpinURL(choice).href);
    return true;
  };
  const choiceForLocation = () => {
    const url = currentURL(), chosen = url.searchParams.get('piece');
    if (url.searchParams.has('piece')) return choices().find(choice => choice.dataset.groupHandle === chosen) || null;
    return choices().find(choice => nativeURL(choice.href).pathname === url.pathname) || null;
  };
  const setSelectionURL = choice => {
    const old = currentURL(), next = nativeURL(picker().dataset.groupEntry);
    next.search = old.search;
    ['variant', 'section_id', 'sections', 'piece'].forEach(key => next.searchParams.delete(key));
    next.searchParams.set('piece', choice.dataset.groupHandle);
    next.hash = old.hash;
    history.pushState({ mobaiGroupChoice: choice.dataset.groupHandle }, '', next);
  };
  const switchChoice = async (choice, { updateHistory = true, focus = true, restoring = false } = {}) => {
    const oldSection = section(), oldPicker = picker();
    if (!oldSection || !oldPicker || !choice) return;
    if (choice.dataset.groupHandle === oldPicker.dataset.groupCurrent) {
      controller?.abort(); generation++; pendingChoice = null;
      setBusy(false);
      const fallback = oldPicker.querySelector('[data-group-fallback]');
      if (fallback) fallback.hidden = true;
      if (updateHistory) setSelectionURL(choice);
      releaseInitialCapture();
      return;
    }
    const choiceKey = [choice.dataset.groupProduct, choice.dataset.groupVariant, oldSection.dataset.groupLocale, oldSection.dataset.groupCurrency].join(':');
    if ((isHairpin(oldSection) || isC01(oldSection)) && pendingChoice === choiceKey && controller && !controller.signal.aborted) return;
    controller?.abort();
    setBusy(false);
    controller = new AbortController();
    const ownController = controller;
    pendingChoice = choiceKey;
    const ownGeneration = ++generation, expectedId = choice.dataset.groupProduct, expectedVariant = choice.dataset.groupVariant, expectedFamily = oldPicker.dataset.groupFamily;
    const requested = nativeURL(choice.href, oldPicker);
    requested.searchParams.set('section_id', oldPicker.dataset.groupSectionId);
    const preview = currentURL().searchParams.get('preview_theme_id') || oldSection.dataset.groupPreviewTheme;
    if (preview) requested.searchParams.set('preview_theme_id', preview);
    if (isC01(oldSection)) prepareC01URL(requested, oldSection);
    const c01RequestContext = isC01(oldSection) ? c01Context(oldSection) : null;
    setBusy(true, oldPicker.dataset.groupLoading);
    const timeout = setTimeout(() => ownController.abort(), 20000);
    try {
      let responseHTML;
      if (c01RequestContext) responseHTML = await c01Requests.load(requested, c01RequestContext, ownController.signal);
      else {
        const response = await fetch(requested.pathname + requested.search, { credentials: 'same-origin', cache: 'no-store', signal: ownController.signal });
        if (!response.ok) throw new Error('group_http_' + response.status);
        responseHTML = await response.text();
      }
      const parsed = new DOMParser().parseFromString(responseHTML, 'text/html');
      const nextSection = parsed.querySelector('[data-group-product-section]'), nextPicker = nextSection?.querySelector('[data-group-picker]');
      if (!nextSection || !nextPicker || nextSection.dataset.groupProduct !== expectedId || nextPicker.dataset.groupFamily !== expectedFamily || nextPicker.dataset.groupCurrent !== choice.dataset.groupHandle || nextSection.dataset.groupLocale !== oldSection.dataset.groupLocale || nextSection.dataset.groupCurrency !== oldSection.dataset.groupCurrency) throw new Error('group_context');
      const form = nextSection.querySelector('#mobai-product-form'), variantField = form?.querySelector('[name=id]');
      if (!form || variantField?.value !== expectedVariant) throw new Error('group_variant');
      if (ownGeneration !== generation) return;
      // Resolve the target responsive main image before replacing the visible section.
      // H01 warms images only; its section data uses the uncached request above.
      if (isHairpin(oldSection)) {
        const mainImage = nextSection.querySelector('#mobai-main-image'), media = window.mobaiH01Media;
        if (!mainImage || !media) throw new Error('group_image_missing');
        let abortReady;
        try {
          await Promise.race([
            media.prepare(media.fromImage(mainImage), 'high'),
            new Promise((resolve, reject) => {
              abortReady = () => reject(new Error('group_image_aborted'));
              ownController.signal.addEventListener('abort', abortReady, { once: true });
              if (ownController.signal.aborted) abortReady();
            })
          ]);
        } finally { ownController.signal.removeEventListener('abort', abortReady); }
        if (ownGeneration !== generation || ownController.signal.aborted || !oldSection.isConnected) return;
      }
      nextSection.querySelectorAll('script:not([type="application/ld+json"])').forEach(script => script.remove());
      const imported = document.importNode(nextSection, true);
      if (updateHistory) setSelectionURL(choice);
      stickyCleanup();
      oldSection.replaceWith(imported);
      if (isHairpin(imported)) {
        const enquiry = imported.querySelector('.mobai-whatsapp-product'), floating = document.querySelector('[data-whatsapp-float]');
        if (enquiry && floating) floating.href = enquiry.href;
      }
      blockedButtons.clear();
      setBusy(false);
      releaseInitialCapture();
      initializeSticky(); caption(); syncSaved();
      if (focus) section().querySelector(`[data-group-handle="${choice.dataset.groupHandle}"]`)?.focus({ preventScroll: true });
      document.dispatchEvent(new CustomEvent('mobai:product-choice', { detail: { productId: expectedId, variantId: expectedVariant, family: expectedFamily } }));
    } catch (error) {
      if (c01RequestContext && ['group_context', 'group_variant'].includes(error.message)) c01Requests.invalidate(requested, c01RequestContext);
      if (ownGeneration !== generation) return;
      if (!restoring) setBusy(false);
      const status = section()?.querySelector('[data-group-status]'), fallback = section()?.querySelector('[data-group-fallback]');
      if (status) { status.textContent = oldPicker.dataset.groupFailed; status.hidden = false; }
      if (fallback) { fallback.href = nativeURL(choice.href, oldPicker).href; fallback.hidden = false; }
    } finally { clearTimeout(timeout); if (ownGeneration === generation) pendingChoice = null; }
  };
  document.addEventListener('click', event => {
    const choice = event.target.closest?.('[data-group-choice]');
    if (!choice || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (usesSnapshots(choice.closest('[data-group-product-section]'))) {
      event.preventDefault();
      if (choice.dataset.groupHandle !== picker()?.dataset.groupCurrent) switchSnapshot(choice);
      return;
    }
    if (isHairpin(choice.closest('[data-group-product-section]'))) {
      choice.href = hairpinURL(choice).href;
      return;
    }
    event.preventDefault();
    switchChoice(choice);
  });
  document.addEventListener('submit', event => {
    const form = event.target.closest?.('#mobai-product-form');
    if (form?.closest('[data-group-product-section]')?.dataset.groupPending === 'true') event.preventDefault();
  }, true);
  const rejectUnknownChoice = () => {
    controller?.abort(); generation++; pendingChoice = null;
    setBusy(false);
    setBusy(true, picker().dataset.groupFailed);
    const fallback = section().querySelector('[data-group-fallback]');
    const displayed = choices().find(choice => choice.dataset.groupHandle === picker().dataset.groupCurrent);
    if (fallback && displayed) { fallback.href = nativeURL(displayed.href).href; fallback.hidden = false; }
  };
  window.addEventListener('popstate', () => {
    if (usesSnapshots(section())) {
      if (snapshotAddPending()) { history.replaceState({ mobaiNativeProduct: section().dataset.groupProduct }, '', committedSnapshotURL || currentURL().href); return; }
      const choice = choiceForLocation();
      if (!choice || !switchSnapshot(choice, { updateHistory: false, focus: false, restoring: true })) {
        setBusy(true, picker()?.dataset.groupFailed || '');
        location.assign(currentURL().href);
      }
      return;
    }
    if (isHairpin(section())) { restoreHairpinLocation(); return; }
    const choice = choiceForLocation();
    if (choice) switchChoice(choice, { updateHistory: false, focus: false, restoring: true });
    else if (picker() && currentURL().searchParams.has('piece')) rejectUnknownChoice();
  });
  const initialize = () => {
    if (!picker()) return;
    if (usesSnapshots(section())) {
      registerSnapshots();
      normalizeSnapshotLinks();
      const requested = choiceForLocation();
      if (requested && currentURL().searchParams.has('piece')) {
        if (!switchSnapshot(requested, { updateHistory: true, replaceHistory: true, focus: false, restoring: true })) {
          setBusy(true, picker().dataset.groupFailed);
          location.replace(hairpinURL(requested).href);
        }
      } else if (!requested && currentURL().searchParams.has('piece')) rejectUnknownChoice();
      else { setBusy(false); releaseInitialCapture(); initializeSticky(); caption(); syncSaved(); committedSnapshotURL = currentURL().href; }
      return;
    }
    if (isHairpin(section())) {
      choices().forEach(choice => { choice.href = hairpinURL(choice).href; });
      if (restoreHairpinLocation()) return;
      setBusy(false); releaseInitialCapture();
      initializeSticky(); caption(); syncSaved();
      return;
    }
    initializeSticky(); caption(); syncSaved();
    const requested = choiceForLocation();
    if (requested) switchChoice(requested, { updateHistory: false, focus: false, restoring: true });
    else if (currentURL().searchParams.has('piece')) rejectUnknownChoice();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true }); else initialize();
})();


